// ============================================================================
// ClientActivityLog — the client's "log my own exercise" form (lime / own).
// ----------------------------------------------------------------------------
// A client records an exercise they did on their own: name it (free text — the
// trainer's exercise catalog isn't exposed to the client realm), pick a type,
// enter sets/reps/weight OR a duration/distance, and a performed-at date. Posts
// to /api/client/me/activities (the backend forces source='own', status=
// 'completed') and invalidates the activity + calendar views so the home screen
// and the client calendar update immediately. Mobile-first, Pulse, EN + 繁中.
// ============================================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Banner, Button, Card, DateField, TextField } from "../../components";
import { useLogClientActivity } from "../hooks";
import type { ClientActivityInput } from "../../api";
import { todayIso } from "./portalLib";

function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** ISO date ("YYYY-MM-DD") -> a local-midnight instant the API normalizes to UTC. */
function dayToIso(day: string): string | undefined {
  const s = day.trim();
  if (!s) return undefined;
  const d = new Date(`${s}T00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function ClientActivityLog({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation("activity");
  const log = useLogClientActivity();

  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [when, setWhen] = useState(todayIso());
  const [sets, setSets] = useState("");
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  const [duration, setDuration] = useState("");
  const [distance, setDistance] = useState("");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);
  const [needTitle, setNeedTitle] = useState(false);

  const reset = () => {
    setTitle("");
    setType("");
    setWhen(todayIso());
    setSets("");
    setReps("");
    setWeight("");
    setDuration("");
    setDistance("");
    setNotes("");
  };

  const submit = () => {
    setSaved(false);
    setNeedTitle(false);
    const name = title.trim();
    if (!name) {
      setNeedTitle(true);
      return;
    }
    const body: ClientActivityInput = { title: name };
    const performed = dayToIso(when);
    if (performed) body.performed_at = performed;
    if (type.trim()) body.type = type.trim();
    const setsN = numOrUndef(sets);
    if (setsN !== undefined) body.sets = setsN;
    const repsN = numOrUndef(reps);
    if (repsN !== undefined) body.reps = repsN;
    const weightN = numOrUndef(weight);
    if (weightN !== undefined) body.weight_kg = weightN;
    const durN = numOrUndef(duration);
    if (durN !== undefined) body.duration_min = durN;
    const distN = numOrUndef(distance);
    if (distN !== undefined) body.distance = distN;
    if (notes.trim()) body.notes = notes.trim();

    log.mutate(body, {
      onSuccess: () => {
        setSaved(true);
        reset();
        if (onDone) window.setTimeout(onDone, 900);
      },
    });
  };

  return (
    <Card className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-bold text-body">{t("log.title")}</h2>
        <p className="mt-0.5 text-sm text-body-soft">{t("log.subtitle")}</p>
      </div>

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t("log.freeTextLabel")}
          placeholder={t("log.freeTextPlaceholder")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (needTitle) setNeedTitle(false);
          }}
          hint={t("log.searchHint")}
        />

        <TextField
          label={t("log.type")}
          placeholder={t("log.typePlaceholder")}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />

        <DateField label={t("log.performedAt")} value={when} onChange={setWhen} />

        <div className="grid grid-cols-3 gap-4">
          <TextField
            label={t("log.sets")}
            value={sets}
            onChange={(e) => setSets(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
          />
          <TextField
            label={t("log.reps")}
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
          />
          <TextField
            label={t("log.weight")}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.5"
            min={0}
            suffix={t("units.kg")}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("log.duration")}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            type="number"
            inputMode="numeric"
            min={0}
            suffix={t("units.min")}
          />
          <TextField
            label={t("log.distance")}
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            type="number"
            inputMode="decimal"
            step="0.1"
            min={0}
            suffix={t("units.km")}
          />
        </div>

        <div className="w-full">
          <label
            htmlFor="vf-act-notes"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("log.notes")}
          </label>
          <textarea
            id="vf-act-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder={t("log.notesPlaceholder")}
            className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>

        {needTitle && (
          <Banner tone="warning" title={t("log.needTitle")} />
        )}
        {log.isError && (
          <Banner tone="danger" title={t("log.saveError")}>
            {t("log.saveErrorBlurb")}
          </Banner>
        )}
        {saved && <Banner tone="success" title={t("log.saved")} />}

        <Button type="submit" block size="lg" disabled={log.isPending}>
          {log.isPending ? t("log.saving") : t("log.save")}
        </Button>
      </form>
    </Card>
  );
}
